/**
 * @file Right-pane composition: `<TerminalHeader/>` on top, the live
 *       `<TerminalView/>` below, plus role-aware chrome (observer
 *       banner + demote toast) introduced in Phase 2b.
 * @module @labcraft/lab/ui/components/terminal-pane
 *
 * Subscribes to the {@link Terminal} service's status for the
 * connection-pill color, role for the observer-mode banner, and the
 * driver→observer transition for a 3-second "another tab took the
 * wheel" toast. The toast only fires when the tab held the driver
 * role for ≥ 2 seconds — brief drive-bys (paste-then-undo from a
 * sibling tab) don't spam.
 */

import { useEffect, useRef, useState, type JSX } from 'react'
import type { SubscriptionStatus } from '@labcraft/core/frontend'
import type {
  BudgetExhaustedPayload,
  MachineLifecycleState,
  Role,
} from '../../shared/ws_messages.ts'
import type { Terminal } from '../../client/chapter.ts'
import { readLearnerEvents } from '../learner_events.ts'
import { ExpiryWarningBanner } from './expiry_warning_banner.tsx'
import { TerminalHeader } from './terminal_header.tsx'
import { TerminalOverlay } from './terminal_overlay.tsx'
import { TerminalView } from './terminal_view.tsx'
import styles from './terminal_pane.module.css'

/** How long a tab must hold driver before a demotion shows a toast. */
const DEMOTE_TOAST_THRESHOLD_MS = 2_000

/** How long the demote toast stays on screen before fading. */
const DEMOTE_TOAST_DURATION_MS = 3_000

/**
 * Props for {@link TerminalPane}.
 *
 * @property terminal - The frontend `Terminal` service to subscribe
 *   to (status, role, presence).
 * @property envLabel - Label rendered in the {@link TerminalHeader}
 *   (e.g. "Nix · 02-ephemeral-shells").
 */
export type TerminalPaneProps = {
  terminal: Terminal
  envLabel: string
  /**
   * Called when the learner clicks the Pause button. Wired by the
   * chapter page to `lab.machines.suspend(machineId)` (Phase 3's
   * `POST /api/machines/:machineId/suspend` proxy). Absent → no
   * Pause button rendered.
   */
  onPause?: () => void
  /**
   * Called when the learner clicks "Start a fresh one" in the
   * `expired` overlay. Wired by the chapter page to engagement
   * restart. Absent → button hidden.
   */
  onExpiredRestart?: () => void
  /**
   * Called when the learner clicks "Keep alive" in the pre-expiry
   * warning banner. Wired by the chapter page to
   * `lab.chapter.touchEngagement()`. Absent → no banner is rendered
   * even if `expiresAt` is in the warning window.
   */
  onKeepAlive?: () => void
  /**
   * Called whenever the underlying WS subscription status changes (and
   * once on mount with the initial status). Lets a parent — e.g.
   * {@link WorkspacePane} — drive a per-machine live/idle tab dot from
   * the real wire state. Absent → status stays private to this pane.
   */
  onStatusChange?: (status: SubscriptionStatus) => void
}

export function TerminalPane(props: TerminalPaneProps): JSX.Element {
  const [status, setStatus] = useState<SubscriptionStatus>(
    props.terminal.status,
  )
  const [role, setRole] = useState<Role | null>(props.terminal.role)
  const [lifecycleState, setLifecycleState] =
    useState<MachineLifecycleState | null>(props.terminal.lifecycleState)
  const [expiresAt, setExpiresAt] = useState<number | null>(
    props.terminal.expiresAt,
  )
  const [clearKey, setClearKey] = useState(0)
  const [toastVisible, setToastVisible] = useState(false)
  // #834: set once a `budget-exhausted` frame arrives; drives the
  // upgrade overlay + read-only terminal until the page navigates away.
  const [budgetExhausted, setBudgetExhausted] =
    useState<BudgetExhaustedPayload | null>(null)
  // #1333: latched for the clean-refresh safety-net fresh-boot — set when
  // a `content-refreshed` lifecycle frame arrives (the saved session's
  // pinned content was force-expired), cleared once the fresh VM is
  // interactive (`running`). Drives the explicit content-refresh overlay.
  const [contentRefreshed, setContentRefreshed] = useState(false)

  // Tracking state that must not trigger re-renders on every flip.
  const prevRoleRef = useRef<Role | null>(props.terminal.role)
  const driverSinceRef = useRef<number | null>(
    props.terminal.role === 'driver' ? Date.now() : null,
  )
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Mirror status into local state for the connection pill AND notify
  // the parent (so a tab strip can render a live/idle dot from the real
  // wire state). The `onStatusChange` callback identity may change per
  // render; keep it in a ref so this effect doesn't re-subscribe and
  // tear down the status listener on every parent render.
  const onStatusChangeRef = useRef(props.onStatusChange)
  onStatusChangeRef.current = props.onStatusChange
  useEffect(() => {
    onStatusChangeRef.current?.(props.terminal.status)
    return props.terminal.onStatus(next => {
      setStatus(next)
      onStatusChangeRef.current?.(next)
    })
  }, [props.terminal])

  useEffect(() => {
    return props.terminal.onLifecycle(frame => {
      setLifecycleState(frame.state)
      // #1333: latch the clean-refresh signal for the whole fresh-boot
      // sequence (terminating → provisioning → … → running) and drop it
      // once the fresh VM is interactive.
      if (frame.reason === 'content-refreshed') setContentRefreshed(true)
      else if (frame.state === 'running') setContentRefreshed(false)
      // Re-emit onto the window-mounted LearnerEvents bus at the
      // commit boundary so Playwright specs assert against the
      // backend-mirrored event names. Lifecycle frame is the source
      // of truth; the semantic `suspend` / `resume` / `expired`
      // events are derived from `frame.state` so a spec waiting on
      // `suspend` doesn't need to filter the broader `lifecycle`
      // stream.
      const bus = readLearnerEvents()
      if (bus) {
        bus.emit('lifecycle', { state: frame.state })
        if (
          frame.state === 'pausing' ||
          frame.state === 'paused' ||
          frame.state === 'snapshotting' ||
          frame.state === 'saved'
        ) {
          // #1056: the frame now carries the real trigger `reason` from
          // the hypervisor (`idle-suspend` / `voluntary-pause` / …), so
          // the bus event reports the truth instead of the pre-#1056
          // hardcoded `'idle'` guess. `voluntary-pause` maps to the
          // learner-facing `'voluntary'`; everything else is `'idle'`.
          bus.emit('suspend', {
            reason: frame.reason === 'voluntary-pause' ? 'voluntary' : 'idle',
          })
        } else if (frame.state === 'resuming' || frame.state === 'running') {
          bus.emit('resume')
        } else if (frame.state === 'terminating') {
          bus.emit('expired')
        }
      }
    })
  }, [props.terminal])

  // #1059: the engagement-TTL deadline arrives on its own frame now, so
  // the warning-banner countdown subscribes to `onEngagement` rather
  // than reading `expiresAt` off the lifecycle frame.
  useEffect(() => {
    return props.terminal.onEngagement(setExpiresAt)
  }, [props.terminal])

  useEffect(() => {
    const unsub = props.terminal.onRole(next => {
      const prev = prevRoleRef.current
      prevRoleRef.current = next
      setRole(next)

      if (next === 'driver') {
        driverSinceRef.current = Date.now()
        return
      }
      if (prev === 'driver' && next === 'observer') {
        const since = driverSinceRef.current
        driverSinceRef.current = null
        const tenureMs = since !== null ? Date.now() - since : 0
        if (tenureMs >= DEMOTE_TOAST_THRESHOLD_MS) {
          setToastVisible(true)
          if (toastTimerRef.current !== null) {
            clearTimeout(toastTimerRef.current)
          }
          toastTimerRef.current = setTimeout(() => {
            setToastVisible(false)
            toastTimerRef.current = null
          }, DEMOTE_TOAST_DURATION_MS)
        }
      }
    })
    return () => {
      unsub()
      if (toastTimerRef.current !== null) {
        clearTimeout(toastTimerRef.current)
        toastTimerRef.current = null
      }
    }
  }, [props.terminal])

  // #834: surface the daily-budget conversion moment. The frame fires
  // once (pre-open refusal or mid-session exhaustion); latch it so the
  // upgrade overlay persists even as later lifecycle frames (saved,
  // terminating) flow in behind it.
  useEffect(() => {
    return props.terminal.onBudgetExhausted(payload => {
      setBudgetExhausted(payload)
    })
  }, [props.terminal])

  // Pause makes sense only when the VM is running. While suspending /
  // suspended / resuming / expired the button is hidden via
  // pauseDisabled so an idempotent double-tap doesn't accidentally
  // wake a still-suspending VM.
  const pauseDisabled = lifecycleState !== 'running'

  return (
    <section className={styles.pane} aria-label="Terminal">
      <TerminalHeader
        status={status}
        envLabel={props.envLabel}
        onClear={() => setClearKey(k => k + 1)}
        // Reset semantics is a 600 ms animation + no-op for MVP; the
        // header owns the timing internally.
        onReset={() => {}}
        onPause={props.onPause}
        pauseDisabled={pauseDisabled}
      />
      <div className={styles.viewport}>
        <TerminalView terminal={props.terminal} clearKey={clearKey} />
        <TerminalOverlay
          state={lifecycleState}
          onExpiredRestart={props.onExpiredRestart}
          budgetExhausted={budgetExhausted}
          contentRefreshed={contentRefreshed}
        />
        {props.onKeepAlive && (
          <ExpiryWarningBanner
            expiresAt={expiresAt}
            onKeepAlive={props.onKeepAlive}
          />
        )}
      </div>
      {role === 'observer' && (
        <div className={styles.observerBanner} role="status" aria-live="polite">
          Observing — typing takes the wheel.
        </div>
      )}
      {toastVisible && (
        <div className={styles.toast} role="status" aria-live="polite">
          Another tab took the wheel.
        </div>
      )}
    </section>
  )
}
